import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import { ButtonAction } from "../../../components/NewButtons";
import { StyledForm } from "../../../components/StyledForm";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { useNavigate } from "react-router";
import { useDuplicateProduct } from "../../products/service/products-api";
import {
  useCreateMenu,
  usePublishMenuDraft,
  useUpdateMenuDraftProducts,
} from "../service/menus-api";
import { Menu, UpdateMenuDraftProducts } from "../models/Menus";
import { menuHttp } from "../service/menus-http";
import { routes } from "../../../appRoutingHelpers";
import { toastr } from "react-redux-toastr";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { LoadingModal } from "../../../components/LoadingModal";
import { getErrorMessage } from "../../../error-handler";

type Props = {
  onClose: () => void;
  isOpen: boolean;
  menuItem: Menu | undefined;
};

type FormValues = { suffix: string };

export const DuplicateMenuAndProductsForm = ({
  onClose,
  isOpen,
  menuItem,
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [
    isLoadingDuplicationProductsAndMenu,
    setIsLoadingDuplicationProductsAndMenu,
  ] = useState<boolean>(false);

  const methods = useForm<FormValues>({
    defaultValues: {
      suffix: "",
    },
  });

  const navigate = useNavigate();
  const { duplicateProduct } = useDuplicateProduct();
  const { mutateAsync: createMenu } = useCreateMenu();
  const { mutateAsync: updateMenuDraftProducts } = useUpdateMenuDraftProducts();
  const { mutateAsync: publishMenu } = usePublishMenuDraft();

  // HEAVY OPERATION
  const duplicateMenuAndProducts = async ({ suffix }: FormValues) => {
    setIsLoadingDuplicationProductsAndMenu(true);
    if (!menuItem) {
      return;
    }
    try {
      // Fetch the current active products for the menu
      const menuProducts = await menuHttp.getMenuProducts(
        organizationKeyName,
        menuItem.id
      );
      const duplicatedProducts: UpdateMenuDraftProducts["data"]["products"] = [];

      for (const product of menuProducts) {
        const id = await duplicateProduct(product.id, suffix);
        if (id) {
          duplicatedProducts.push({
            id: id,
            sortKey: product.sortKey,
            appearance: product.appearance,
          });
        }
      }

      // Create an identical menu
      const id = await createMenu({
        data: {
          countryCode: menuItem.countryAlpha2Code,
          internalNote: menuItem?.internalNote || "",
          name: `Duplicate of ${menuItem.name}`,
          isMenuOverwritesEnabled: false,
          currency: menuItem.currency,
        },
        organizationKeyName,
      });

      // Update menu and save as draft
      await updateMenuDraftProducts({
        data: {
          products: duplicatedProducts,
        },
        id,
        organizationKeyName,
      });

      // Publish it
      await publishMenu({
        id,
        organizationKeyName,
      });
      navigate(routes.menuEdit(organizationKeyName, id));
      toastr.success("Success", "Menu successfully duplicated");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    } finally {
      setIsLoadingDuplicationProductsAndMenu(false);
      onClose();
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <ConfirmActionModal
          isOpen={isOpen}
          onClose={onClose}
          width={600}
          cancelButtonText="Cancel"
          title="Duplicate menu and products"
          subtitle="Create a duplicate menu and create a duplicate of each product in the menu."
          confirmButton={
            <ButtonAction
              onClick={methods.handleSubmit(duplicateMenuAndProducts)}
              disabled={isLoadingDuplicationProductsAndMenu}
              type="button"
            >
              {isLoadingDuplicationProductsAndMenu ? (
                <ActionSpinner />
              ) : (
                "Duplicate menu and products"
              )}
            </ButtonAction>
          }
        >
          <StyledForm>
            <TextInput
              name="suffix"
              label="Suffix"
              helperText="Will be appended to the admin name of the product. E.g. entering '(duplicate)' for the product 'Salad' will duplicate the exact product, but call it 'Sales (duplicate)' to easily find it in the product list."
            />
          </StyledForm>
        </ConfirmActionModal>
      </FormProvider>
      <LoadingModal
        header={"Duplicating menu and products..."}
        isOpen={isLoadingDuplicationProductsAndMenu}
      />
    </>
  );
};
